import { useMutation } from '@tanstack/react-query';
import { GUIDE_KEYS, useActiveWidget } from '../../queries/guides';
import { useGuideStore } from '../../stores/guideStore';
import { useInspectorStore } from '../../stores/inspectorStore';
import {
  FigmaWidget,
  FigmaWidgetSettings,
  Page,
  StyleSheet,
} from '../../types';
import { handleMutationSuccess, updateGuideVersion } from '../helpers';
import { queryClient } from '../../constants/app';
import { findIndex, merge } from 'lodash';
import { produce } from 'immer';
import {
  editFigmaWidgetPropertiesApi,
  editFigmaWidgetSourceApi,
} from '../../helpers/api/widgets/figma';
import { editFigmaWidgetStyleCommand } from '../../helpers/api/commands/widgets/figmaWidgetCommands';
import { guideWriteCommandApi } from '../../helpers/api/guidelinesApi';
import { optimisticallyUpdateWidgetStyle } from './collections';

export const useFigmaWidgetMutations = () => {
  const guideId = useGuideStore(s => s.activeGuideId) as string;
  const pageId = useGuideStore(s => s.activePageId) as string;
  const chapterId = useGuideStore(s => s.activeChapterId) as string;
  const guideVersion = useGuideStore(s => s.activeGuideVersion) as number;
  const sectionId = useGuideStore(s => s.activeSectionId) as string;
  const columnIndex = useInspectorStore(
    s => s.widgetSettings?.columnIndex,
  ) as number;
  const widgetId = useInspectorStore(s => s.widgetSettings?.widgetId) as string;
  const figmaWidget = useActiveWidget() as FigmaWidget;
  return {
    editSource: useMutation(
      ({ source }: { source: string }) => {
        return editFigmaWidgetSourceApi({
          guideId,
          chapterId,
          pageId,
          sectionId,
          widgetId,
          columnIndex,
          guideVersion,
          source,
        });
      },
      {
        onMutate: variables => {
          updateGuideVersion(guideId);

          optimisticallyUpdateFigmaWidget({
            guideId,
            pageId,
            sectionId,
            columnIndex,
            widgetId,
            value: { sourceId: variables.source },
          });
        },
        onSuccess: response => {
          handleMutationSuccess('Source Edited in Figma Widget', response, {
            entityId: guideId,
          });
        },
      },
    ),
    editStyle: useMutation(
      (options: { style: string }) => {
        const command = editFigmaWidgetStyleCommand({
          aggregateId: guideId,
          aggregateVersion: guideVersion,
          chapterId: chapterId,
          sectionId,
          columnIndex,
          widgetId,
          pageId: pageId,
          widgetStyle: {
            stylesheet: options.style,
            language: 'css',
            version: 3,
          } as StyleSheet,
        });
        return guideWriteCommandApi(command);
      },
      {
        onMutate: variables => {
          updateGuideVersion(guideId);
          optimisticallyUpdateWidgetStyle(
            guideId,
            pageId,
            sectionId,
            columnIndex,
            widgetId,
            variables.style,
          );
        },
        onSuccess: data => {
          handleMutationSuccess(null, data, {
            entityId: guideId,
          });
        },
      },
    ),
    editProperties: useMutation(
      ({
        property,
      }: {
        property: { [K in keyof FigmaWidgetSettings]?: FigmaWidgetSettings[K] };
      }) => {
        return editFigmaWidgetPropertiesApi({
          guideId,
          chapterId,
          pageId,
          sectionId,
          widgetId: widgetId,
          columnIndex,
          guideVersion,
          settings: { ...figmaWidget.settings, ...property },
        });
      },
      {
        onMutate: variables => {
          updateGuideVersion(guideId);
          optimisticallyUpdateFigmaWidget({
            guideId,
            pageId,
            sectionId,
            columnIndex,
            widgetId,
            value: {
              settings: { ...figmaWidget.settings, ...variables.property },
            },
          });
        },
        onSuccess: response => {
          handleMutationSuccess('Properties Edited in Figma Widget', response, {
            entityId: guideId,
          });
        },
      },
    ),
  };
};

const optimisticallyUpdateFigmaWidget = ({
  guideId,
  pageId,
  sectionId,
  columnIndex,
  widgetId,
  value,
}: {
  guideId: string;
  pageId: string;
  sectionId: string;
  columnIndex: number;
  widgetId: string;
  value: { [key: string]: unknown };
}) => {
  queryClient.setQueryData(
    [GUIDE_KEYS.PAGE_BY_ID, guideId, pageId],
    (old: Page) => {
      const sectionIndex = findIndex(
        old.sections,
        section => section.id === sectionId,
      );

      const widgetIndex = findIndex(
        old.sections[sectionIndex].columns[columnIndex].widgets,
        widget => widget.id === widgetId,
      );

      if (old) {
        return produce(old, draft => {
          const figmaWidget = old.sections[sectionIndex].columns[columnIndex]
            .widgets[widgetIndex] as FigmaWidget;
          draft.sections[sectionIndex].columns[columnIndex].widgets[
            widgetIndex
          ] = merge(figmaWidget, value);
        });
      }
    },
  );
};
